import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { DTN_API } from 'configs/endpoints';
import { connectionTimeout } from 'configs/initialValues';
import { dtnTokenManager } from './auth';
import {
  DTNCatalog,
  DTNLegendList,
  DTNMetadata,
  DTNStyleList,
  DTNTimestampWrapper,
} from './types';

type DTNTimestampResponse = DTNTimestampWrapper[];

const dtnApi = createApi({
  reducerPath: 'dtn-api',
  baseQuery: fetchBaseQuery({
    baseUrl: DTN_API,
    timeout: connectionTimeout,
    prepareHeaders: async (headers) => {
      const token = await dtnTokenManager.getToken();
      headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept-Language', 'en-US');
      return headers;
    },
  }),
  tagTypes: ['Catalog', 'Timestamps', 'Metadata', 'Legend', 'Styles'],
  endpoints: (builder) => ({
    getCatalog: builder.query<DTNCatalog, void>({
      query: () => '/catalog',
      transformResponse: (raw: DTNCatalog) => raw,
      providesTags: ['Catalog'],
    }),

    getTimestamps: builder.query<DTNTimestampResponse, { layerId: string }>({
      query: ({ layerId }) => `/timestamps/${layerId}`,
      providesTags: (_result, _error, { layerId }) => [{ type: 'Timestamps', id: layerId }],
    }),

    getMetadata: builder.query<DTNMetadata, { layerId: string }>({
      query: ({ layerId }) => `/metadata/${layerId}`,
      providesTags: (_result, _error, { layerId }) => [{ type: 'Metadata', id: layerId }],
    }),

    getStyles: builder.query<DTNStyleList, { layerId: string }>({
      query: ({ layerId }) => ({
        url: `/styles/${layerId}`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { layerId }) => [{ type: 'Styles', id: layerId }],
    }),

    getLegend: builder.query<DTNLegendList, { layerId: string }>({

      query: ({ layerId }) => ({
        url: `/legends/${layerId}`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { layerId }) => [{ type: 'Legend', id: layerId }],
    }),
  }),
});

export const {
  useGetCatalogQuery,
  useGetTimestampsQuery,
  useGetMetadataQuery,
  useGetLegendQuery,
  useGetStylesQuery,
  useLazyGetTimestampsQuery,
} = dtnApi;

export default dtnApi;
